import { Layer, tile, View, Globe } from "@hscmap/stellar-globe";
import { CreateElement, VNode } from "vue";
import { mixins } from "vue-class-component";
import { Component, Inject, Vue, Prop, Watch } from "vue-property-decorator";
import { LayerComponentBase } from "./layer_component_base";
import { GlobeComponentType } from "./Globe/script";
import { KEYS } from "./key";

class TileLayer extends Layer {
  tr!: tile.Renderer

  constructor(readonly tp: tile.TextureProvider, readonly tlc: TileLayerComponent) {
    super()
  }

  onAdd() {
    this.tr = new tile.Renderer(this.globe, this.tp)
    this.setCacheSize()
  }

  onRemove() {
    this.tr.release()
  }

  alpha(view: View) {
    return Layer.baseAlpha(view)
  }

  render(view: View) {
    if (this.tlc.show) {
      this.tr.alpha = this.alpha(view)
      this.tr.integerLevel = this.tlc.integerLevel
      this.tr.lodBias = this.tlc.lodBias
      this.tr.animationLod = this.tlc.animationLod
      this.tr.render(view)
    }
  }

  setCacheSize(cacheSize = this.tlc.cacheSize) {
    this.tlc.cacheSize = cacheSize
    if (this.globe) {
      this.tr.setCacheSize(this.tlc.cacheSize)
    }
  }
}

@Component({
  provide() {
    return { [KEYS.TileLayerComponent]: this }
  },
})
export class TileLayerComponent extends LayerComponentBase<TileLayer> {
  @Prop({ default: 0 })
  lodBias!: number
  @Watch('lodBias')
  onLodBiasChange() {
    this.globe.requestRefresh()
  }

  @Prop({ default: 1 })
  animationLod!: number
  @Watch('animationLod')
  onanimationLodChange() {
    this.globe.requestRefresh()
  }

  @Prop({ default: false })
  integerLevel!: boolean
  @Watch('integerOrder')
  onintegerOrderChange() {
    this.globe.requestRefresh()
  }

  @Prop({ default: 1000 })
  cacheSize!: number
  @Watch('cacheSize')
  onCacheSizeChange() {
    this.layer.setCacheSize(this.cacheSize)
  }

  @Prop({ default: true })
  show!: boolean
  @Watch('show')
  onShowChange() {
    this.globe.requestRefresh()
  }

  tpSet!: Set<tile.TextureProvider>

  created() {
    this.tpSet = new Set()
  }

  get tp(): tile.TextureProvider { // TODO: cleanup
    console.assert(this.tpSet.size === 1, 'tpSet.size must be 1')
    let tp: tile.TextureProvider
    this.tpSet.forEach((_) => tp = _)
    return tp!
  }

  newLayer() {
    const layer = new TileLayer(this.tp, this)
    layer.setCacheSize(this.cacheSize)
    return layer
  }
}

// @ts-ignore
@mixins
@Component
export class TileTextureProviderComponentBase<TP extends tile.TextureProvider> extends Vue {
  @Inject(KEYS.TileLayerComponent)
  private layerComponent!: TileLayerComponent

  @Inject(KEYS.GlobeComponent)
  globeComponent!: GlobeComponentType

  @Prop({ default: 3 })
  refreshLod!: number

  tp!: TP

  get globe(): Globe {
    return this.globeComponent.globe
  }

  protected refreshTextureProvider() {
    if (this.tp) {
      this.layerComponent.tpSet.delete(this.tp)
      this.tp.release()
    }
    this.tp = (this as any).newTextureProvider()
    this.layerComponent.tpSet.add(this.tp)
  }

  mounted() {
    // tslint:disable-next-line:max-line-length
    console.assert(!!(this as any).newTextureProvider, `${this.constructor.name} must have newTextureProvider(): => TP extends tile.TextureProvider`)
    console.assert(!!this.layerComponent, `${this.constructor.name} must be a child of TileLayerComponent`)
    console.assert(!!this.globeComponent, `${this.constructor.name} must be a child of GlobeComponent`)
    this.refreshTextureProvider()
  }

  beforeDestroy() {
    this.layerComponent.tpSet.delete(this.tp)
    this.tp.release()
  }

  render(h: CreateElement): VNode {
    return h()
  }

  clearTiles() {
    this.layerComponent.layer.tr.clearTiles()
  }

  clearInvisibleTiles() {
    const view = this.globe.viewFactory.view()
    this.layerComponent.layer.tr.clearInvisibleTiles(
      view,
      Math.log(this.globe.viewFactory.retina ? devicePixelRatio : 1) / Math.LN2 + this.refreshLod)
  }

  updateParams(full = false) {
    if (full) {
      this.clearTiles()
      this.tp.update()
      this.globe.requestRefresh()
    } else {
      this.clearInvisibleTiles()
      this.tp.update()
      this.globe.requestRefresh()
    }
  }
}

export { TileLayer as StellarGlobeTileLayer }
